import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Mail } from "lucide-react";
import { api, errMsg, passwordScore } from "@/lib/api";
import { PasswordMeter } from "@/components/PasswordMeter";
import { Logo } from "@/components/Logo";

const field = "w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", guardian_name: "", guardian_email: "" });
  const [confirm, setConfirm] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [show, setShow] = useState(false);
  const strength = passwordScore(form.password);
  const mismatch = confirm.length > 0 && confirm !== form.password;
  const canSubmit = form.name.trim() && form.email.trim() && strength.valid && !mismatch && confirm && accepted;
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, guardian_email: form.guardian_email.trim() || null, accepted_terms: accepted };
      const { data } = await api.post("/auth/signup", payload);
      setDone(data);
      toast.success("Account created");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="min-h-screen grain flex items-center justify-center px-4 py-12">
        <div className="relative z-10 w-full max-w-xl reveal" data-testid="signup-done-panel">
          <Logo size="md" testId="signup-logo-done" />
          <h1 className="mt-8 text-3xl font-extrabold">You're in</h1>
          <p className="mt-3 text-sm text-slate-500">{done.message}</p>

          {done.guardian_verification_link && (
            <div className="mt-8 card-flat p-6" data-testid="signup-guardian-block">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                <p className="rule-label">Invite for your guardian</p>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Send this link to {form.guardian_email} so they can set their own password. We can't email it for them yet.
              </p>
              <Link
                data-testid="signup-guardian-link"
                to={done.guardian_verification_link}
                className="mt-3 block break-all font-mono-x text-xs font-semibold text-blue-700 hover:underline"
              >
                {done.guardian_verification_link}
              </Link>
            </div>
          )}

          <Link
            to="/login"
            data-testid="signup-goto-login"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Sign in and begin
          </Link>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 py-12">
      <div className="relative z-10 w-full max-w-lg reveal">
        <Logo size="md" testId="signup-logo" />
        <h1 className="mt-8 text-3xl font-extrabold">Create your student account</h1>
        <p className="mt-2 text-sm text-slate-500">
          Your plan is yours. A guardian is welcome but never required — nobody has to approve it but you.
        </p>
        <form onSubmit={submit} className="mt-8 card-flat p-7 space-y-4">
          <div>
            <label className="rule-label">Your name</label>
            <input data-testid="signup-name" className={`${field} mt-2`} value={form.name} onChange={set("name")} required />
          </div>
          <div>
            <label className="rule-label">Your email</label>
            <input data-testid="signup-email" type="email" className={`${field} mt-2`} value={form.email} onChange={set("email")} required />
          </div>
          <div>
            <label className="rule-label">Password</label>
            <div className="relative">
              <input
                data-testid="signup-password"
                type={show ? "text" : "password"}
                minLength={8}
                className={`${field} mt-2 pr-16`}
                value={form.password}
                onChange={set("password")}
                required
              />
              <button
                type="button"
                data-testid="signup-password-toggle"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 translate-y-[2px] text-xs font-semibold text-slate-500 hover:text-slate-900"
              >
                {show ? "Hide" : "Show"}
              </button>
            </div>
            <PasswordMeter value={form.password} />
          </div>
          <div>
            <label className="rule-label">Confirm password</label>
            <input
              data-testid="signup-password-confirm"
              type={show ? "text" : "password"}
              className={`${field} mt-2`}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            {mismatch && <p className="mt-1.5 text-xs text-rose-600" data-testid="signup-password-mismatch">Those two don't match yet.</p>}
          </div>
          <div className="pt-4 border-t border-slate-200">
            <p className="rule-label">Guardian — optional</p>
            <p className="mt-2 text-xs text-slate-500">Leave these empty if you don't want one. You can add one later.</p>
            <input data-testid="signup-guardian-name" className={`${field} mt-3`} value={form.guardian_name} onChange={set("guardian_name")} placeholder="Guardian name (optional)" />
            <input data-testid="signup-guardian-email" type="email" className={`${field} mt-2`} value={form.guardian_email} onChange={set("guardian_email")} placeholder="Guardian email (optional)" />
          </div>
          <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              data-testid="signup-accept-terms"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-blue-600"
            />
            <span>
              I agree to the{" "}
              <Link to="/terms" target="_blank" className="font-semibold text-blue-700 hover:underline">Terms &amp; Conditions</Link> and the{" "}
              <Link to="/privacy" target="_blank" className="font-semibold text-blue-700 hover:underline">Privacy Policy</Link>.
            </span>
          </label>
          <button data-testid="signup-submit" disabled={busy || !canSubmit} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {busy ? "Creating…" : "Create account"}
          </button>
          <p className="flex items-start gap-2 text-xs text-slate-500">
            <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
            We store only what learning needs: your age, your goal and your progress. Minimal-data policy for minors.
          </p>
        </form>
      </div>
    </div>
  );
}
